import { RefObject } from 'react';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { titleCase } from '@frontend/string';
import { NO_COVERAGE, OUT_OF_NETWORK } from '../constants';
import type { ServiceHistory } from '../types/api-response-type';
import type {
  Filters,
  CoinsuranceTableDataType,
  CopaymentTableDataType,
  DeductibleTableDataType,
  SummaryDataType,
} from '../types/tables-data-type';

export const titleCaseWrapper = (str?: string) => {
  const modifiedStr = str
    ?.replaceAll(/([-_])/g, ' ')
    .split(' ')
    .reduce((str, i) => (i.trim() ? str + ` ${i.trim()}` : str), '')
    .trim();
  return modifiedStr ? titleCase(modifiedStr) : '-';
};

export const convertJsonToString = (
  obj?: Record<string, string | number> | Record<string, string | number>[]
): string => {
  const arr: string[] = [];
  if (isEmpty(obj)) return '';
  else if (Array.isArray(obj)) {
    obj.forEach((o) => {
      arr.push(convertJsonToString(o));
    });
  } else {
    for (const key in obj) {
      arr.push(`${key}: ${obj[key]}`);
    }
  }
  return arr.join(', ');
};

export const convertServiceDatesIntoServiceHistory = (
  serviceDates: string[],
  procedureCode: string
): ServiceHistory[] => {
  return serviceDates.map((date) => ({
    ...(procedureCode && { procedure_code: procedureCode }),
    service_date: date,
  }));
};

export const isArrayOfStrings = (obj?: any[]) => {
  if (!obj) return false;
  return Array.isArray(obj) && obj?.every((item) => typeof item === 'string');
};

export const filterTableData = <T extends CopaymentTableDataType | DeductibleTableDataType>({
  data = [],
  showNetworkFilter,
  filters,
  applyFilters = true,
}: {
  data?: T[];
  showNetworkFilter: boolean;
  filters: Filters;
  applyFilters?: boolean;
}) => {
  if (applyFilters && (showNetworkFilter || filters?.coverage?.length > 0)) {
    return data.filter((row) => {
      const isInNetwork = !row?.network?.includes(OUT_OF_NETWORK);
      const matchesNetworkFilter = !showNetworkFilter || isInNetwork === filters.inNetworkSelected;

      const coverageLevel = !row?.coverageLevel || row?.coverageLevel === '-' ? NO_COVERAGE : row?.coverageLevel;
      const matchesCoverageFilter = !filters.coverage.length || filters.coverage.includes(coverageLevel);

      return matchesNetworkFilter && matchesCoverageFilter;
    });
  }

  return data;
};

export const filterCoinsuranceTableData = ({
  data = [],
  showNetworkFilter,
  filters,
  applyFilters = true,
}: {
  data?: CoinsuranceTableDataType[];
  showNetworkFilter: boolean;
  filters: Filters;
  applyFilters?: boolean;
}): CoinsuranceTableDataType[] => {
  if (applyFilters && showNetworkFilter) {
    return data.filter((row) => filters.inNetworkSelected === !row?.network?.includes(OUT_OF_NETWORK));
  }

  return data;
};

export const filterSummaryData = ({
  data = [],
  showNetworkFilter,
  filters,
  applyFilters = true,
}: {
  data?: SummaryDataType[];
  showNetworkFilter: boolean;
  filters: Filters;
  applyFilters?: boolean;
}): SummaryDataType[] => {
  if (applyFilters && (filters.coverage.length > 0 || showNetworkFilter)) {
    return data?.filter((row) => {
      const isInNetwork = !row?.metadata?.network?.includes(OUT_OF_NETWORK);
      const matchesNetworkFilter = !showNetworkFilter || isInNetwork === filters.inNetworkSelected;

      const coverageLevel =
        !row?.metadata?.coverageLevel || row?.metadata?.coverageLevel === '-'
          ? NO_COVERAGE
          : row?.metadata?.coverageLevel;
      const matchesCoverageFilter = !filters.coverage.length || filters.coverage.includes(coverageLevel);

      return matchesNetworkFilter && matchesCoverageFilter;
    });
  }

  return data;
};

export const getHTMLBlobData = (ref: RefObject<HTMLDivElement>): Blob => {
  const newDoc = document.implementation.createHTMLDocument('PDF');
  Array.from(document.styleSheets).forEach((styleSheet: CSSStyleSheet) => {
    const isDifferentOriginStyles = styleSheet.href && new URL(styleSheet.href).origin !== window.location.origin;
    if (styleSheet.ownerNode && !isDifferentOriginStyles && styleSheet.cssRules.length) {
      const styleElement = document.createElement('style');
      const cssRulesArray = Array.from(styleSheet.cssRules);
      styleElement.textContent = cssRulesArray.reduce((acc, rule) => acc + (rule?.cssText ?? ''), '');
      newDoc.head.appendChild(styleElement);
    }
  });
  Array.from(document.fonts).forEach((font) => newDoc.fonts.add(font));
  const blob = new Blob([`<html>${newDoc.head.innerHTML}${ref?.current?.innerHTML}</html>`], {
    type: 'text/html',
  });
  return blob;
};

function processArrayBuffer(buffer: Buffer | ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  // Convert the int array to a binary string
  // We loop through the bytes, instead of using the Array in an apply(), since
  // String.fromCharCode().apply() hits a maximum size when the bytelength
  // is more than 65536 (which is the limit for apply()).
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return binary;
}

export function arrayBufferToBase64(buffer: Buffer | ArrayBuffer) {
  // Convert to base64
  return window.btoa(processArrayBuffer(buffer));
}

export const getDaysFromCurrentDate = (targetDateString: Date | string, currentDate?: Date | string): number => {
  if (!targetDateString) return 0;

  const targetDateObj = dayjs(targetDateString);
  const currentDateObj = currentDate ? dayjs(currentDate) : dayjs(); // Current date and time

  if (!targetDateObj.isValid()) {
    return 0;
  }

  const daysDifference = targetDateObj.diff(currentDateObj, 'day');
  return daysDifference;
};

type AddressFields = {
  addressLine1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
};
export const getFormattedAddress = ({
  addressLine1 = '',
  city = '',
  state = '',
  zipCode = '',
}: AddressFields): string => {
  return `${addressLine1 || ''}, ${city || ''}, ${state || ''}, ${zipCode || ''}`
    .trim()
    .replace(/,\s*,/g, ',') // Replace double commas with a single comma
    .replace(/,(\s*)$/, ''); // Remove trailing comma
};
